import { Injectable } from '@angular/core';
import {
    CanActivate,
    CanMatch,
    Route,
    Router,
    UrlSegment,
    UrlTree,
    ActivatedRouteSnapshot,
    RouterStateSnapshot,
} from '@angular/router';
import { Observable, of, switchMap } from 'rxjs';
import { AuthService } from 'app/core/auth/auth.service';

@Injectable({
    providedIn: 'root',
})
export class AuthGuard implements CanActivate, CanMatch {
    constructor(private _authService: AuthService, private _router: Router) {}

    /**
     * Can activate
     */
    canActivate(
        route: ActivatedRouteSnapshot,
        state: RouterStateSnapshot
    ):
        | Observable<boolean | UrlTree>
        | Promise<boolean | UrlTree>
        | boolean
        | UrlTree {
        return this._check(state.url);
    }

    /**
     * Can match
     */
    canMatch(
        route: Route,
        segments: UrlSegment[]
    ):
        | Observable<boolean | UrlTree>
        | Promise<boolean | UrlTree>
        | boolean
        | UrlTree {
        const url = '/' + segments.map((s) => s.path).join('/');
        return this._check(url);
    }

    /**
     * Check the authenticated status, user verification, and employee profile completeness
     */
    private _check(targetUrl: string): Observable<boolean | UrlTree> {
        return this._authService.check().pipe(
            switchMap((authenticated) => {
                // If NOT authenticated
                if (!authenticated) {
                    return of(this._router.createUrlTree(['/sign-in']));
                }

                const isVerified = this._authService.isUserVerified();
                const isPendingVerificationRoute =
                    targetUrl.startsWith('/pending-verification') ||
                    targetUrl === 'pending-verification';
                const isCompleteProfileRoute =
                    targetUrl.startsWith('/complete-profile') ||
                    targetUrl === 'complete-profile';

                // If user is unverified
                if (!isVerified) {
                    if (isPendingVerificationRoute) {
                        return of(true);
                    }
                    return of(
                        this._router.createUrlTree(['/pending-verification'])
                    );
                }

                // If user is verified, check if employee profile is completed
                const isProfileCompleted = this._authService.isProfileCompleted();

                // If verified but mandatory profile not completed yet
                if (!isProfileCompleted) {
                    if (isCompleteProfileRoute) {
                        return of(true);
                    }
                    return of(this._router.createUrlTree(['/complete-profile']));
                }

                // If user is verified and profile is completed, prevent re-visiting pending/onboarding pages
                if (isPendingVerificationRoute || isCompleteProfileRoute) {
                    return of(this._router.createUrlTree(['/dashboard']));
                }

                // Allow access
                return of(true);
            })
        );
    }
}
